import type {
  CoverageHistoryMode,
  CoverageHistorySummary,
  CoverageHistoryTrendPoint,
} from '@/components/smartai/api/getCoverageHistory';

export type CoverageMetricKind = 'lines' | 'inventory';

export type CoverageChartMetric = CoverageMetricKind | 'both';

/** Maps chart toggle to a single measure (``both`` is handled in bar chart UI). */
export function chartMetricToKind(metric: CoverageChartMetric): CoverageMetricKind {
  return metric === 'inventory' ? 'inventory' : 'lines';
}

export function pctFromSummary(
  summary: CoverageHistorySummary | undefined,
  kind: CoverageMetricKind,
): number | null {
  const block =
    kind === 'lines' ? summary?.linesOfCode : summary?.inventory;
  const p = block?.totalCoverage?.percentage;
  if (p === undefined || p === null || Number.isNaN(Number(p))) {
    return null;
  }
  return Number(p);
}

export function formatProcessedAt(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDelta(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

/** Primary build identifier for chart axis labels (prefers build number). */
export function trendBuildLabel(point: CoverageHistoryTrendPoint): string | null {
  const buildNumber = point.buildNumber;
  if (buildNumber != null && String(buildNumber).trim() !== '') {
    return String(buildNumber).trim();
  }
  const buildId = point.buildId;
  if (buildId != null && String(buildId).trim() !== '') {
    return String(buildId).trim();
  }
  return null;
}

/** X-axis / short label: build number first, then scan or timestamp fallbacks. */
export function trendPointLabel(point: CoverageHistoryTrendPoint): string {
  const build = trendBuildLabel(point);
  if (build) return build;
  if (point.scanNumber != null) {
    return `Scan #${point.scanNumber}`;
  }
  if (point.runNumber != null) {
    return `Run ${point.runNumber}`;
  }
  return formatProcessedAt(point.processedAt);
}

function parseTrendPointDate(raw?: string): Date | null {
  if (raw == null || raw === '') return null;
  const text = String(raw).trim();
  if (!text) return null;
  let d = new Date(text);
  if (Number.isNaN(d.getTime()) && /^\d{4}-\d{2}-\d{2}T/.test(text)) {
    d = new Date(`${text}Z`);
  }
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Compact date for chart x-axis (build completion / scan time). */
export function formatTrendBuildDate(iso?: string): string | null {
  const d = parseTrendPointDate(iso);
  if (!d) return null;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Short axis date e.g. Sep 15 (coverage-by-build bars). */
export function formatTrendBuildAxisDateShort(iso?: string): string | null {
  const d = parseTrendPointDate(iso);
  if (!d) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Bar chart x-axis for run-level trend: Run N + short date. */
export function trendRunBarAxisTickLines(
  point: CoverageHistoryTrendPoint,
): string[] {
  if (point.runNumber != null) {
    const date = formatTrendBuildAxisDateShort(point.processedAt);
    const line1 = `Run ${point.runNumber}`;
    return date ? [line1, date] : [line1];
  }
  const date = formatTrendBuildAxisDateShort(point.processedAt);
  const fallback = formatProcessedAt(point.processedAt);
  return date ? [fallback, date] : [fallback];
}

/** Bar chart x-axis: #buildNumber + short date. */
export function trendBuildBarAxisTickLines(
  point: CoverageHistoryTrendPoint,
): string[] {
  const buildNumber = point.buildNumber;
  if (buildNumber != null && String(buildNumber).trim() !== '') {
    const date = formatTrendBuildAxisDateShort(point.processedAt);
    const line1 = `#${String(buildNumber).trim()}`;
    return date ? [line1, date] : [line1];
  }
  return trendChartAxisTickLines(point);
}

export function formatCountCompact(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return n.toLocaleString();
}

export function stackCountsFromTrendPoint(
  point: CoverageHistoryTrendPoint,
  kind: CoverageMetricKind,
): { covered: number; uncovered: number; total: number } {
  const dash = point.dashboard;
  if (kind === 'lines') {
    const loc = point.summary?.linesOfCode?.totalCoverage;
    const covered =
      dash?.coveredExecutableLines ?? loc?.covered ?? 0;
    const total =
      dash?.executableLinesInCoverage ?? loc?.total ?? 0;
    const uncovered = Math.max(0, total - covered);
    return { covered, uncovered, total: covered + uncovered };
  }
  const inv = point.summary?.inventory?.totalCoverage;
  const invDash = dash?.inventoryTotalCoverage;
  const covered = invDash?.covered ?? inv?.covered ?? 0;
  const total = invDash?.total ?? inv?.total ?? 0;
  const uncovered = Math.max(0, total - covered);
  return { covered, uncovered, total: covered + uncovered };
}

/** Chart.js x-axis ticks: line 1 = build number, line 2 = build date (array = multiline). */
export function trendChartAxisTickLines(
  point: CoverageHistoryTrendPoint,
): string[] {
  const buildNumber = point.buildNumber;
  const hasBuildNumber =
    buildNumber != null && String(buildNumber).trim() !== '';
  const buildLine = hasBuildNumber ? String(buildNumber).trim() : null;
  const dateLine = formatTrendBuildDate(point.processedAt);

  if (buildLine && dateLine) return [buildLine, dateLine];
  if (buildLine) return [buildLine];

  const fallback = trendPointLabel(point);
  if (dateLine) return [fallback, dateLine];
  return [fallback];
}

/** Tooltip title: build plus scan context when both exist. */
export function trendPointTooltipTitle(point: CoverageHistoryTrendPoint): string {
  const build = trendBuildLabel(point);
  const parts: string[] = [];
  if (build) parts.push(`Build ${build}`);
  if (point.scanNumber != null) {
    parts.push(`Scan #${point.scanNumber}`);
  }
  const when = formatProcessedAt(point.processedAt);
  if (when && when !== '—') parts.push(when);
  if (parts.length) return parts.join(' · ');
  return trendPointLabel(point);
}

export function compareFromTrendPoints(
  points: CoverageHistoryTrendPoint[],
): {
  previous?: CoverageHistoryTrendPoint | null;
  latest?: CoverageHistoryTrendPoint | null;
  delta: {
    linesOfCodePercentage: number | null;
    inventoryPercentage: number | null;
  };
} {
  if (!points.length) {
    return {
      previous: null,
      latest: null,
      delta: { linesOfCodePercentage: null, inventoryPercentage: null },
    };
  }
  const latest = points[points.length - 1];
  const previous = points.length >= 2 ? points[points.length - 2] : null;
  const prevLoc = previous
    ? pctFromSummary(previous.summary, 'lines')
    : null;
  const latLoc = pctFromSummary(latest.summary, 'lines');
  const prevInv = previous
    ? pctFromSummary(previous.summary, 'inventory')
    : null;
  const latInv = pctFromSummary(latest.summary, 'inventory');
  const deltaLoc =
    prevLoc != null && latLoc != null ? latLoc - prevLoc : null;
  const deltaInv =
    prevInv != null && latInv != null ? latInv - prevInv : null;
  return {
    previous,
    latest,
    delta: {
      linesOfCodePercentage: deltaLoc,
      inventoryPercentage: deltaInv,
    },
  };
}

export const HISTORY_MODE_LABELS: Record<CoverageHistoryMode, string> = {
  combined: 'Combined',
  automation: 'Automation',
  manual: 'Manual',
};

/** Timeline runs are automation or manual executions, never combined. */
export function formatRunSourceType(sourceType?: string | null): string {
  const s = (sourceType ?? '').trim().toLowerCase();
  if (s === 'automation') return 'Automation';
  if (s === 'manual') return 'Manual';
  return '—';
}
